/* eslint-disable jsx-a11y/no-noninteractive-element-interactions -- role="dialog" 백드롭이 Tab 가둠 키보드를 받는다 (useModalA11y 가 제공, Modal 아톰 정합) */
import { useState } from 'react';
import { useModalA11y } from '@/shared/lib/hooks';
import { renderOutreachDraftHtml, type OutreachDraftContent } from '@aegislab/shared';
import { Button } from '@/shared/ui/atoms';
import { SegmentedControl } from '@/shared/ui/SegmentedControl';
import * as s from './OutreachPreviewOverlay.css';

type PreviewWidth = 'desktop' | 'mobile';

export interface OutreachPreviewOverlayProps {
  content: OutreachDraftContent;
  formUrl: string | null;
  imageUrl: string | null;
  productName: string | null;
  onClose: () => void;
}

/**
 * 전체 미리보기 — 설계 `docs/design/ia/creator-recruitment-ia.md` §3-5 (퀘스트 5519dc5e CP4).
 *
 * **테스트 발송과 같은 렌더러로 그린다.** CP3 까지는 React 캔버스를 읽기 전용으로 재사용했는데,
 * 실제로 나가는 것은 메일 HTML 이라 둘이 다른 결과였다. 이제 `renderOutreachDraftHtml` 하나가
 * 미리보기와 테스트 메일을 함께 든다.
 *
 * 링크는 열지 않는다(`linkMode: 'inert'`) — 목적지만 글로 보인다. iframe 은 스크립트도 이동도
 * 막아 둔다: 본문이 사람이 쓴 값이라 미리보기가 실행 자리가 되면 안 된다.
 *
 * **작성기와 발송 확인이 같이 쓴다**(2026-09-29 에 `features/outreach-composer` 에서 내려왔다).
 * 승인 시안 ConfirmAfter 개선안이 발송 확인 오른쪽에도 이 입구를 두는데, 두 feature 는 형제라
 * 서로 import 할 수 없어 초안을 그리는 이 일을 entity 층으로 내렸다.
 */
export function OutreachPreviewOverlay({
  content,
  formUrl,
  imageUrl,
  productName,
  onClose,
}: OutreachPreviewOverlayProps) {
  const [width, setWidth] = useState<PreviewWidth>('desktop');
  const { overlayRef, panelRef, onKeyDown } = useModalA11y<HTMLDivElement>(onClose);

  const html = renderOutreachDraftHtml(content, {
    formUrl,
    imageUrl,
    linkMode: 'inert',
    /*
      실제로 나가는 메일에는 같은 글자에 스티비 수신거부 주소가 걸린다. 여기서는 링크를 만들지
      않는다 — 이 면은 어떤 링크도 열지 않는다. 문구를 실제와 같게 두는 것이 요점이라, 「여기
      링크가 들어갑니다」 같은 설명을 넣으면 푸터 생김새가 달라져 눈으로 대조할 수 없다.
    */
    unsubscribe: { note: '수신거부 Unsubscribe' },
    productName,
  });

  return (
    <div
      ref={(el) => {
        overlayRef.current = el;
        panelRef.current = el;
      }}
      className={s.backdrop}
      role="dialog"
      aria-modal="true"
      aria-label="전체 미리보기"
      tabIndex={-1}
      onKeyDown={onKeyDown}
    >
      <header className={s.head}>
        <SegmentedControl<PreviewWidth>
          segments={[
            { id: 'desktop', label: '데스크톱' },
            { id: 'mobile', label: '모바일' },
          ]}
          activeId={width}
          onSelect={setWidth}
          ariaLabel="미리보기 폭"
        />
        <span className={s.note}>링크는 열리지 않습니다 — 목적지만 확인합니다.</span>
        <Button variant="ghost" size="xs" onClick={onClose} style={{ marginLeft: 'auto' }}>
          미리보기 닫기
        </Button>
      </header>
      <div className={s.body}>
        <iframe
          title="메일 미리보기"
          className={width === 'desktop' ? s.desktop : s.mobile}
          sandbox=""
          srcDoc={html}
        />
      </div>
    </div>
  );
}
